import { createSelector } from 'reselect'

import { toSafeArray } from '../helpers/StateHelper'
import { normalizeMaterialCode } from '../helpers/StringHelper'
import { DEFAULT_PAGE_SIZE } from './substituteMaterialsActions'

/**
 * The catalog as loaded, whole and unfiltered.
 */
export const substituteMaterialsSelector = createSelector(
	(state) => state.substituteMaterials.get('items'),
	(items) => {
		let result = items ? toSafeArray(items) : []
		return result ? result : []
	}
)

/**
 * The four fields a search matches against: code and description of both sides of the relation.
 */
const SEARCHABLE_FIELDS = ['ObsoleteMaterialCode', 'ObsoleteMaterialDescription', 'SubstituteMaterialCode', 'SubstituteMaterialDescription']

/**
 * Whether a row matches the search text.
 *
 * Partial and case-insensitive, evaluated against the code and the description of the discontinued
 * material and of its substitute.
 * @param {*} row
 * @param {*} query
 */
export const matchesSearch = (row, query) => {
	if (!query || String(query).trim().length === 0) return true
	if (!row) return false

	let needle = String(query)
		.trim()
		.toLowerCase()
	return SEARCHABLE_FIELDS.some((field) => {
		let value = row[field]
		return value
			? String(value)
					.toLowerCase()
					.indexOf(needle) !== -1
			: false
	})
}

/**
 * Compare two rows by one column, case-insensitively.
 * @param {*} a
 * @param {*} b
 * @param {*} field
 */
const compareBy = (a, b, field) => {
	let left = a && a[field] ? String(a[field]).toLowerCase() : ''
	let right = b && b[field] ? String(b[field]).toLowerCase() : ''
	if (left === right) return 0
	return left < right ? -1 : 1
}

/**
 * Resolve one page of the catalog in the client.
 *
 * `GET /SubstituteMaterials` answers the whole catalog with no paging, sorting or filtering on the
 * wire, so all three are derived here. That is viable because the catalog is a small, bounded
 * reference table — around two hundred relations — and it keeps the listing working off the same
 * single read that the spare parts request flow already needs in full.
 *
 * Returns the rows of the requested page plus the pagination the view renders, so the caller never
 * has to recompute the total or clamp the page number itself.
 *
 * @param {*} items the whole catalog
 * @param {*} criteria { query, sortBy, sortDirection, pageNumber } as read from the URL
 * @param {*} pageSize
 */
export const getCatalogPage = (items, criteria, pageSize = DEFAULT_PAGE_SIZE) => {
	let all = items ? items : []
	let safeCriteria = criteria ? criteria : {}

	let filtered = all.filter((row) => matchesSearch(row, safeCriteria.query))

	if (safeCriteria.sortBy && SEARCHABLE_FIELDS.indexOf(safeCriteria.sortBy) !== -1) {
		let direction = safeCriteria.sortDirection === 'desc' ? -1 : 1
		filtered = filtered.slice().sort((a, b) => compareBy(a, b, safeCriteria.sortBy) * direction)
	}

	let total = filtered.length
	let totalPages = total === 0 ? 0 : Math.ceil(total / pageSize)
	// A page number left over from a wider result set — after a search narrows the catalog, say —
	// would otherwise render an empty page with rows available behind it.
	let requested = safeCriteria.pageNumber ? Number(safeCriteria.pageNumber) : 1
	let pageNumber = Number.isFinite(requested) && requested >= 1 ? requested : 1
	if (totalPages > 0 && pageNumber > totalPages) pageNumber = totalPages

	let start = (pageNumber - 1) * pageSize

	return {
		rows: filtered.slice(start, start + pageSize),
		pagination: {
			pageNumber,
			pageSize,
			total: totalPages,
			totalRows: total,
			orderBy: safeCriteria.sortBy ? safeCriteria.sortBy : null,
			orderType: safeCriteria.sortDirection === 'desc' ? 'desc' : 'asc'
		}
	}
}

/**
 * The page the view renders, memoized.
 *
 * `getCatalogPage` filters, sorts and slices the whole catalog, so calling it straight from the
 * render body redid all three on every keystroke and every unrelated state change. This is the
 * memoized form the rest of the domain uses.
 *
 * The criteria is read field by field rather than taken as an object on purpose: it comes from
 * `queryString.parse(location.search)`, which hands back a NEW object every render, and a
 * memoization keyed on that object would never hit. Keyed on the four primitives inside it, the
 * result only recomputes when the criteria actually changes.
 *
 * @param {*} state
 * @param {*} criteria { query, sortBy, sortDirection, pageNumber } as read from the URL
 */
export const catalogPageSelector = createSelector(
	substituteMaterialsSelector,
	(state, criteria) => (criteria ? criteria.query : undefined),
	(state, criteria) => (criteria ? criteria.sortBy : undefined),
	(state, criteria) => (criteria ? criteria.sortDirection : undefined),
	(state, criteria) => (criteria ? criteria.pageNumber : undefined),
	(items, query, sortBy, sortDirection, pageNumber) => getCatalogPage(items, { query, sortBy, sortDirection, pageNumber })
)

/**
 * Index of discontinued material code -> its direct substitute.
 *
 * Consumed by the spare parts request flow to warn which parts in a SAP listing are discontinued.
 * It lives here, next to the catalog it derives from, rather than in a domain of its own: the
 * catalog is one table with one endpoint, and two stores for it would mean two sources of truth.
 *
 * The index is keyed by the NORMALIZED code so rows whose codes differ only in surrounding
 * whitespace or leading zeros still correlate. Rows whose code normalizes to an empty string are
 * excluded: an empty key would match every code-less spare part in the listing.
 *
 * The substitute is the DIRECT one recorded in the catalog. The chain is deliberately not walked,
 * so a substitute that is itself discontinued does not change what this index reports.
 */
export const substituteMaterialsIndexSelector = createSelector(
	(state) => state.substituteMaterials.get('items'),
	(items) => {
		let index = {}
		let catalog = items ? toSafeArray(items) : []
		if (!catalog) return index

		catalog.forEach((entry) => {
			if (!entry) return

			let key = normalizeMaterialCode(entry.ObsoleteMaterialCode)
			if (key.length === 0) return

			index[key] = {
				Code: entry.SubstituteMaterialCode,
				Description: entry.SubstituteMaterialDescription
			}
		})

		return index
	}
)
